import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { getTeacherStudentsTutorSummary } from "../../tutor/api/teacherTutorAnalyticsApi";
import type { TeacherStudentSummaryItem } from "../../tutor/types/teacherTutorAnalytics";
import {
  getStudentProgress,
  listStudentCommunicationLogs,
  overrideCanDo,
  type CommunicationLogRow,
  type StudentProgressResponse,
} from "../../services/api/canDo";
import { fetchCanDosBundle, type CanDoItem } from "../../modules/tutor/api/tutor.client";
import { Loader2 } from "lucide-react";

function parseStudentGrade(raw: string | null | undefined): number | null {
  if (raw == null || !String(raw).trim()) return null;
  const m = String(raw).match(/\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
}

function formatCanDoStatement(c: Pick<CanDoItem, "statement_en" | "statement_ta">): string {
  const en = (c.statement_en || "").trim();
  const ta = (c.statement_ta || "").trim();
  if (en && ta) return `${en} — ${ta}`;
  return en || ta || "Unnamed outcome";
}

function CanDoStatementMultiSelect({
  catalog,
  selectedIds,
  onChange,
  idToLabel,
  disabled,
}: {
  catalog: CanDoItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  idToLabel: Record<string, string>;
  disabled?: boolean;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return catalog;
    return catalog.filter(
      (c) =>
        (c.statement_en || "").toLowerCase().includes(s) ||
        (c.statement_ta || "").toLowerCase().includes(s)
    );
  }, [catalog, q]);

  const labelForId = (id: string) => {
    const fromCat = catalog.find((x) => x.id === id);
    if (fromCat) return formatCanDoStatement(fromCat);
    const fallback = idToLabel[id];
    if (fallback) return fallback;
    return "Saved outcome";
  };

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-slate-600">Search outcomes</label>
      <input
        type="search"
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
        placeholder="Type English or Tamil…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        disabled={disabled}
      />
      <div className="flex flex-wrap gap-1.5 min-h-[2rem] p-2 rounded-lg bg-slate-50 border border-slate-100">
        {selectedIds.length === 0 ? (
          <span className="text-xs text-slate-500">None selected — pick from the list below.</span>
        ) : (
          selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 max-w-full rounded-full bg-indigo-100 border border-indigo-200 px-2 py-0.5 text-xs text-indigo-950"
            >
              <span className="truncate max-w-[240px]" title={labelForId(id)}>
                {labelForId(id)}
              </span>
              <button
                type="button"
                className="shrink-0 rounded-full hover:bg-indigo-200 w-5 h-5 leading-none text-indigo-900"
                aria-label="Remove outcome"
                disabled={disabled}
                onClick={() => toggle(id)}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
        {filtered.map((c) => {
          const on = selectedIds.includes(c.id);
          return (
            <label
              key={c.id}
              className={`flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 ${on ? "bg-indigo-50/60" : ""}`}
            >
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={on}
                disabled={disabled}
                onChange={() => toggle(c.id)}
              />
              <span className="text-sm text-slate-800 leading-snug">{formatCanDoStatement(c)}</span>
            </label>
          );
        })}
        {filtered.length === 0 && <p className="px-3 py-4 text-sm text-slate-500">No outcomes match this search.</p>}
      </div>
    </div>
  );
}

function HighlightedStudentLine({
  text,
  spans,
}: {
  text?: string;
  spans?: { start: number; end: number; kind: string }[];
}) {
  const t = text || "";
  if (!spans?.length) return <span className="text-slate-800">{t || "—"}</span>;
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let i = 0;
  for (const s of sorted) {
    if (s.start > i) parts.push(<span key={`p-${i}`}>{t.slice(i, s.start)}</span>);
    const cls =
      s.kind === "mistake"
        ? "bg-rose-100 text-rose-900 rounded px-0.5"
        : s.kind === "strength"
          ? "bg-emerald-100 text-emerald-900 rounded px-0.5"
          : "bg-indigo-100 text-indigo-900 rounded px-0.5";
    parts.push(
      <span key={`h-${s.start}-${s.end}`} className={cls}>
        {t.slice(s.start, s.end)}
      </span>
    );
    i = s.end;
  }
  if (i < t.length) parts.push(<span key={`p-end`}>{t.slice(i)}</span>);
  return <span className="text-slate-800 whitespace-pre-wrap break-words">{parts}</span>;
}

function rowLowScore(row: CommunicationLogRow): boolean {
  const p = row.performance;
  if (!p) return false;
  return p.fluency < 0.5 || p.accuracy < 0.5 || p.appropriateness < 0.5;
}

export default function TeacherStudentCommunicationProgressPage() {
  const [students, setStudents] = useState<TeacherStudentSummaryItem[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [studentId, setStudentId] = useState("");
  const [lessonFilter, setLessonFilter] = useState<string>("");
  const [logs, setLogs] = useState<CommunicationLogRow[]>([]);
  const [progress, setProgress] = useState<StudentProgressResponse | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrideRow, setOverrideRow] = useState<CommunicationLogRow | null>(null);
  const [overrideSelectedIds, setOverrideSelectedIds] = useState<string[]>([]);
  const [overrideCatalog, setOverrideCatalog] = useState<CanDoItem[]>([]);
  const [overrideCatalogLoading, setOverrideCatalogLoading] = useState(false);
  const [statementById, setStatementById] = useState<Record<string, string>>({});
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [showCorrections, setShowCorrections] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingStudents(true);
    getTeacherStudentsTutorSummary({ days: 90 })
      .then((res) => {
        if (!cancelled) setStudents(res.summaries ?? []);
      })
      .catch(() => {
        if (!cancelled) setStudents([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStudents(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStudentData = useCallback(async () => {
    if (!studentId) return;
    setLoadingData(true);
    setError(null);
    try {
      const ln = lessonFilter.trim() ? Number(lessonFilter) : undefined;
      const [logRes, prog] = await Promise.all([
        listStudentCommunicationLogs(studentId, {
          lesson_no: ln != null && !Number.isNaN(ln) ? ln : undefined,
          limit: 200,
        }),
        getStudentProgress(studentId),
      ]);
      setLogs(logRes.items ?? []);
      setProgress(prog);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Failed to load";
      setError(msg || "Failed to load");
      setLogs([]);
      setProgress(null);
    } finally {
      setLoadingData(false);
    }
  }, [studentId, lessonFilter]);

  useEffect(() => {
    if (!studentId) return;
    void loadStudentData();
  }, [studentId, lessonFilter, loadStudentData]);

  const skillRows = useMemo(() => progress?.skills ?? [], [progress]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!studentId) {
        setStatementById({});
        return;
      }
      const next: Record<string, string> = {};
      if (progress?.skills?.length) {
        for (const s of progress.skills) {
          if (s.can_do_id && s.statement) next[s.can_do_id] = s.statement;
        }
      }
      const g =
        parseStudentGrade(students.find((x) => x.student_id === studentId)?.student_info?.grade) ??
        logs.find((r) => r.grade != null)?.grade ??
        null;
      if (g == null) {
        if (!cancelled) setStatementById(next);
        return;
      }
      const lessons = new Set<number>();
      logs.forEach((r) => {
        if (r.lesson_no != null && !Number.isNaN(Number(r.lesson_no))) lessons.add(Number(r.lesson_no));
      });
      if (lessons.size === 0) lessons.add(0);
      for (const ln of lessons) {
        try {
          const { can_dos } = await fetchCanDosBundle(g, ln);
          for (const c of can_dos) {
            next[c.id] = formatCanDoStatement(c);
          }
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setStatementById(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, students, logs, progress]);

  useEffect(() => {
    if (!overrideRow) {
      setOverrideCatalog([]);
      setOverrideCatalogLoading(false);
      return;
    }
    const g =
      overrideRow.grade ??
      parseStudentGrade(students.find((x) => x.student_id === studentId)?.student_info?.grade) ??
      null;
    const ln = overrideRow.lesson_no != null && !Number.isNaN(Number(overrideRow.lesson_no)) ? Number(overrideRow.lesson_no) : 0;
    if (g == null) {
      setOverrideCatalog([]);
      setOverrideCatalogLoading(false);
      return;
    }
    let cancelled = false;
    setOverrideCatalogLoading(true);
    void fetchCanDosBundle(g, ln)
      .then((bundle) => {
        if (!cancelled) setOverrideCatalog(bundle.can_dos ?? []);
      })
      .catch(() => {
        if (!cancelled) setOverrideCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setOverrideCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [overrideRow, studentId, students]);

  const submitOverride = async () => {
    if (!overrideRow) return;
    const ids = overrideSelectedIds.filter(Boolean);
    setOverrideBusy(true);
    try {
      await overrideCanDo(overrideRow.conversation_id, ids);
      setOverrideRow(null);
      setOverrideSelectedIds([]);
      await loadStudentData();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Override failed";
      setError(msg);
    } finally {
      setOverrideBusy(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Communication tutor progress"
        subtitle="Per-turn tagged outcomes and performance. Adjust tags when the automatic match was wrong."
      />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4 items-end">
            <label className="text-sm block">
              <span className="text-slate-600">Student</span>
              <select
                className="mt-1 block w-64 max-w-full px-3 py-2 rounded-lg border border-slate-200"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                disabled={loadingStudents}
              >
                <option value="">Select…</option>
                {students.map((s) => (
                  <option key={s.student_id} value={s.student_id}>
                    {s.student_info.display_name || s.student_id}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm block">
              <span className="text-slate-600">Lesson no. (optional)</span>
              <input
                type="number"
                min={0}
                className="mt-1 block w-28 px-3 py-2 rounded-lg border border-slate-200"
                value={lessonFilter}
                onChange={(e) => setLessonFilter(e.target.value)}
                placeholder="All"
                title="Use 0 for Communication Tutor skills, or leave empty for all"
              />
            </label>
            <Button variant="outline" size="sm" onClick={() => void loadStudentData()} disabled={!studentId || loadingData}>
              Refresh
            </Button>
          </CardContent>
        </Card>

        {loadingData && (
          <div className="flex items-center gap-2 text-slate-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading…
          </div>
        )}

        {studentId && !loadingData && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Aggregated skills</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="py-2 pr-4">Can-Do</th>
                      <th className="py-2 pr-4">Fluency</th>
                      <th className="py-2 pr-4">Accuracy</th>
                      <th className="py-2 pr-4">Appropriateness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skillRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-4 text-slate-500">
                          No communication logs yet for this student.
                        </td>
                      </tr>
                    ) : (
                      skillRows.map((s) => {
                        const low =
                          s.avg_performance.fluency < 0.5 ||
                          s.avg_performance.accuracy < 0.5 ||
                          s.avg_performance.appropriateness < 0.5;
                        return (
                          <tr key={s.can_do_id} className={`border-b border-slate-100 ${low ? "bg-amber-50" : ""}`}>
                            <td className="py-2 pr-4 max-w-md">{s.statement}</td>
                            <td className="py-2 pr-4">{s.avg_performance.fluency.toFixed(2)}</td>
                            <td className="py-2 pr-4">{s.avg_performance.accuracy.toFixed(2)}</td>
                            <td className="py-2 pr-4">{s.avg_performance.appropriateness.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">Turn logs</CardTitle>
                <label className="text-xs text-slate-600 flex items-center gap-2">
                  <input type="checkbox" checked={showCorrections} onChange={(e) => setShowCorrections(e.target.checked)} />
                  Show corrections column
                </label>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="py-2 pr-2">When</th>
                      <th className="py-2 pr-2 max-w-xs">Student (highlights)</th>
                      <th className="py-2 pr-2 max-w-md">Tagged outcomes</th>
                      <th className="py-2 pr-2">Fluency</th>
                      <th className="py-2 pr-2">Accuracy</th>
                      <th className="py-2 pr-2">Approp.</th>
                      {showCorrections ? <th className="py-2 pr-2 max-w-xs">Corrections</th> : null}
                      <th className="py-2 pr-2">Audio</th>
                      <th className="py-2 pr-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={showCorrections ? 9 : 8} className="py-4 text-slate-500">
                          No rows.
                        </td>
                      </tr>
                    ) : (
                      logs.map((row) => (
                        <tr
                          key={row.conversation_id}
                          className={`border-b border-slate-100 ${rowLowScore(row) ? "bg-amber-50" : ""}`}
                        >
                          <td className="py-2 pr-2 whitespace-nowrap text-slate-600">
                            {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                          </td>
                          <td className="py-2 pr-2 max-w-[14rem] align-top">
                            <HighlightedStudentLine text={row.student_message} spans={row.highlight_spans} />
                          </td>
                          <td className="py-2 pr-2 max-w-md text-slate-800 align-top text-xs leading-snug">
                            {(() => {
                              const ids = row.effective_can_do_ids || [];
                              if (ids.length === 0) return "—";
                              return (
                                <ul className="list-disc pl-4 space-y-0.5">
                                  {ids.map((id) => {
                                    const text = statementById[id]?.trim();
                                    return (
                                      <li key={id}>
                                        {text ? (
                                          text
                                        ) : (
                                          <span className="text-slate-500">Unknown outcome</span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              );
                            })()}
                          </td>
                          <td className="py-2 pr-2">{(row.performance?.fluency ?? 0).toFixed(2)}</td>
                          <td className="py-2 pr-2">{(row.performance?.accuracy ?? 0).toFixed(2)}</td>
                          <td className="py-2 pr-2">{(row.performance?.appropriateness ?? 0).toFixed(2)}</td>
                          {showCorrections ? (
                            <td className="py-2 pr-2 align-top text-xs text-slate-700 max-w-[12rem]">
                              {row.sentence_corrections && row.sentence_corrections.length > 0
                                ? row.sentence_corrections.slice(0, 3).map((c, i) => (
                                    <div key={i} className="mb-1 border-b border-slate-100 pb-1">
                                      <div className="line-through text-slate-500">{c.original_fragment}</div>
                                      <div>{c.suggested_fragment}</div>
                                    </div>
                                  ))
                                : "—"}
                            </td>
                          ) : null}
                          <td className="py-2 pr-2 text-xs">
                            {row.audio_uri ? (
                              <a className="text-purple-700 underline" href={row.audio_uri} target="_blank" rel="noreferrer">
                                Play
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-2 pr-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setOverrideRow(row);
                                setOverrideSelectedIds([...(row.effective_can_do_ids || [])]);
                              }}
                            >
                              Override
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}

        {overrideRow && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
              <h3 className="font-bold text-slate-900 dark:text-gray-100">Override tagged outcomes</h3>
              <p className="text-xs text-slate-600 dark:text-gray-400">
                {overrideRow.created_at
                  ? `Turn: ${new Date(overrideRow.created_at).toLocaleString()}`
                  : "Selected turn"}
              </p>
              {overrideCatalogLoading ? (
                <div className="flex items-center gap-2 text-slate-600 text-sm py-6">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading outcome list…
                </div>
              ) : (
                <CanDoStatementMultiSelect
                  key={overrideRow.conversation_id}
                  catalog={overrideCatalog}
                  selectedIds={overrideSelectedIds}
                  onChange={setOverrideSelectedIds}
                  idToLabel={statementById}
                  disabled={overrideBusy}
                />
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setOverrideRow(null);
                    setOverrideSelectedIds([]);
                  }}
                  disabled={overrideBusy}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={() => void submitOverride()} disabled={overrideBusy || overrideCatalogLoading}>
                  {overrideBusy ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
